import React from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { CreateCompanyFormData } from "../../schemas/companySchemas";

interface CreateCompanyDetailsSectionProps {
  form: UseFormReturn<CreateCompanyFormData>;
}

export const CreateCompanyDetailsSection: React.FC<CreateCompanyDetailsSectionProps> = ({
  form,
}) => {
  const { control } = form;

  return (
    <div className="space-y-6">
      {/* Row 1: Company Name and Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={control}
          name="companyName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Name *</FormLabel>
              <FormControl>
                <Input placeholder="Enter company name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="companyEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Email</FormLabel>
              <FormControl>
                <Input
                  placeholder="company@example.com"
                  type="email"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Row 2: Website URL */}
      <FormField
        control={control}
        name="websiteUrl"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Website URL</FormLabel>
            <FormControl>
              <Input
                placeholder="https://www.example.com"
                {...field}
                value={field.value || ""}
              />
            </FormControl>
            <FormDescription>
              Enter the full URL including https://
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Row 3: Integration IDs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          control={control}
          name="salesforceId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Salesforce ID</FormLabel>
              <FormControl>
                <Input
                  placeholder="SF-123456"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormDescription>
                External Salesforce identifier
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="intacctId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Intacct ID *</FormLabel>
              <FormControl>
                <Input
                  placeholder="INT-123456"
                  {...field}
                  value={field.value || ""}
                />
              </FormControl>
              <FormDescription>
                External Intacct identifier (required)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Access Controls Section */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium mb-4">Access Controls</h3>
        <div className="space-y-4">
          <FormField
            control={control}
            name="marketingEnabled"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Marketing Enabled
                  </FormLabel>
                  <FormDescription>
                    Allow marketing communications for this company
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="hasEaglesAccess"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    Eagles Access
                  </FormLabel>
                  <FormDescription>
                    Grant access to Eagles-restricted resources
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="hasLATAAccess"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">
                    LATA Access
                  </FormLabel>
                  <FormDescription>
                    Grant access to LATA-restricted resources
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
};